import React, { useEffect, useState } from "react";
import Layout from "../../../layout/Layout";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import EmailConfirmationAlert from "../../../components/Common/EmailConfirmationAlert";
import {
  CalendarMonthOutlined,
  ConfirmationNumberOutlined,
  GroupOutlined,
  LocationOnOutlined,
  MoreVertOutlined,
  NoteOutlined,
  PersonOutlineOutlined,
  ShareOutlined,
} from "@mui/icons-material";
import { colors } from "../../../theme/theme";
import EventParticipantTable from "../../../components/Events/EventParticipantTable";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import EventJoinedSuccess from "../../../components/Modals/EventJoinedSuccess";
import axiosInstance from "../../../utils/config/config";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import EventComments from "../../../components/Events/EventComments";
import { useDispatch, useSelector } from "react-redux";
import { getUserById } from "../../../redux/dispatchers/users.dispatcher";

const EventDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const eventId = location.state?.eventId || null;

  const { userData } = useSelector((state) => state.user);

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);

  const getEventById = async () => {
    try {
      const response = await axiosInstance.get(
        `/event/getEventById?id=${eventId}`
      );

      if (response.status === 200) {
        setEventData(response.data);
        getAllParticipants(response.data?.id);
        console.log("Get Event By Id");
      }
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  };

  const joinEvent = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("event/joinEvent", {
        user_id: userData?.id,
        event_id: eventData?.id,
      });

      if (response.status === 200) {
        setSuccess(true);
        console.log("Event Joined Successfully");
        getEventById();
        dispatch(getUserById());
        getAllParticipants(eventData?.id);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error joining event:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const [allParticipants, setAllParticipants] = useState([]);

  const getAllParticipants = async (id) => {
    try {
      const response = await axiosInstance.get(
        `event/getAllParticipants?eventId=${id}`
      );

      if (response.status === 200) {
        setAllParticipants(response.data);
        console.log("Get Participants");
      }
    } catch (error) {
      console.error("Error fetching event participants:", error);
    }
  };

  useEffect(() => {
    if (!eventId) return;
    getEventById();
  }, []);

  const joinedCheck =
    allParticipants &&
    allParticipants?.some(
      (participant) => participant?.participants?.id === userData?.id
    );

  return (
    <Layout heading="Event Details">
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box>
        <img
          src={eventData?.image_url}
          alt="cover"
          style={{
            height: "150px",
            width: "100%",
            objectFit: "cover",
            borderRadius: "14px",
            marginBottom: "20px",
          }}
        />

        <EmailConfirmationAlert />

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item size={{ xs: 12, md: 7 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {eventData?.interest_tags?.map((tag) => (
                <Typography
                  key={tag}
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Mulish-Light",
                    color: colors.secondary,
                  }}
                >
                  {"#" + tag}
                </Typography>
              ))}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              mb={1.5}
              justifyContent={"space-between"}
            >
              <Typography
                sx={{
                  fontSize: "32px",
                  fontFamily: "Mulish-ExtraBold",
                  color: "#fff",
                }}
              >
                {eventData?.title}
              </Typography>
              <Stack direction="row" spacing={1}>
                <CustomBtn
                  text={joinedCheck ? "Joined" : "Join"}
                  width="100%"
                  height="40px"
                  fontSize="16px"
                  icon={!joinedCheck}
                  noClick={joinedCheck}
                  onClick={joinedCheck ? null : joinEvent}
                />
                <IconButton>
                  <ShareOutlined sx={{ fontSize: 20, color: "#fff" }} />
                </IconButton>
                <IconButton>
                  <MoreVertOutlined sx={{ fontSize: 20, color: "#fff" }} />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction={"row"} spacing={1} mb={1}>
              <NoteOutlined sx={{ fontSize: 16, color: colors.secondary }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#fff",
                  fontFamily: "Mulish-Light",
                }}
              >
                {eventData?.description}
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" spacing={2} mb={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthOutlined
                  sx={{ fontSize: 16, color: colors.secondary }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#fff",
                    fontFamily: "Mulish-Light",
                  }}
                >
                  {dayjs(eventData?.event_date).format("DD MMM, YYYY")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocationOnOutlined
                  sx={{ fontSize: 16, color: colors.secondary }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#fff",
                    fontFamily: "Mulish-Light",
                    textTransform: "capitalize",
                  }}
                >
                  {eventData?.event_type || eventData?.location}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <GroupOutlined sx={{ fontSize: 16, color: colors.secondary }} />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#fff",
                    fontFamily: "Mulish-Light",
                  }}
                >
                  {allParticipants?.length || 0} people
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ConfirmationNumberOutlined
                  sx={{ fontSize: 16, color: colors.secondary }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#fff",
                    fontFamily: "Mulish-Light",
                    textTransform: "capitalize",
                  }}
                >
                  {eventData?.ticketing_option}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <PersonOutlineOutlined
                sx={{ fontSize: 16, color: colors.secondary }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#fff",
                  fontFamily: "Mulish-Light",
                }}
              >
                Organized by{" "}
                <Typography
                  component="span"
                  sx={{
                    color: colors.secondary,
                    fontSize: "12px",
                    fontFamily: "Mulish-Light",
                    "&:hover": {
                      cursor: "pointer",
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => navigate("/profile/" + eventData?.User?.id)}
                >
                  {"@" + eventData?.User?.firstName}
                </Typography>
              </Typography>
            </Stack>
          </Grid>
          <Grid item size={{ xs: 12, md: 5 }}>
            <EventComments eventData={eventData} getEventById={getEventById} />
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <EventParticipantTable
              eventData={eventData}
              allParticipants={allParticipants}
            />
          </Grid>
        </Grid>
      </Box>

      <EventJoinedSuccess
        open={success}
        handleClose={() => setSuccess(false)}
      />
    </Layout>
  );
};

export default EventDetails;
